import React, { ReactNode, useEffect } from 'react';
import { Popup } from 'devextreme-react/popup';
import { Button } from './Button';
import { ComponentProps } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faX } from '@fortawesome/free-solid-svg-icons';
import '../../scss/controls/_genericPopup.scss';

type ButtonProps = ComponentProps<typeof Button>;

export const GenericPopup: React.FC<{
    id: string;
    isOpen: boolean;
    children: ReactNode;
    actionButtons?: ButtonProps[];
    title?: any;
    showCloseButton?: boolean;
    showTitle?: boolean;
    width?: string;
    height?: string;
    minHeight?: string;
    maxWidth?: string;
    maxHeight?: string;
    handleClose?: () => void;
    buttonAlignment?: string;
    hideOnOutsideClick?: boolean;
    wrapTitle?: boolean;
    useCustomTitleRender?: boolean;
}> = ({
    id,
    isOpen,
    children,
    actionButtons,
    title,
    showCloseButton = false,
    showTitle = false,
    width = 'auto',
    height = 'auto',
    minHeight = 'auto',
    maxWidth = 'auto',
    maxHeight,
    handleClose,
    buttonAlignment = 'center',
    hideOnOutsideClick = false,
    wrapTitle = false,
    useCustomTitleRender = false,
}) => {
    const wrapCustomRender = () => (
        <div style={{ display: 'flex', width: '100%' }}>
            <div style={{ whiteSpace: 'normal', wordWrap: 'break-word', width: '100%' }}>{title}</div>
            {showCloseButton && (
                <div
                    style={{ width: '28px', height: '32px', float: 'right', cursor: 'pointer' }}
                    onClick={handleClose}
                >
                    <div style={{ borderRadius: '24px', backgroundColor: '#EAEEFF', textAlign: 'center' }}>
                        <FontAwesomeIcon
                            key='customPopupCloseButton'
                            icon={faX}
                            color='#464646'
                            width='10px'
                        />
                    </div>
                </div>
            )}
        </div>
    );

    useEffect(() => {
        // Lock body scroll when modal is open (especially important for mobile)
        if (isOpen) {
            const scrollY = window.scrollY;
            document.body.style.position = 'fixed';
            document.body.style.top = `-${scrollY}px`;
            document.body.style.width = '100%';
            document.body.style.overflow = 'hidden';

            return () => {
                // Restore scroll position
                document.body.style.position = '';
                document.body.style.top = '';
                document.body.style.width = '';
                document.body.style.overflow = '';
                window.scrollTo(0, scrollY);
            };
        }
    }, [isOpen]);

    return (
        <>
            <Popup
                id={id}
                visible={isOpen}
                title={!useCustomTitleRender ? title : ''}
                showCloseButton={showCloseButton}
                showTitle={showTitle}
                width={width}
                height={height}
                minHeight={minHeight}
                maxWidth={maxWidth}
                maxHeight={maxHeight}
                dragEnabled={false}
                onHiding={handleClose}
                hideOnOutsideClick={hideOnOutsideClick}
                titleRender={wrapTitle ? wrapCustomRender : undefined}
            >
                {maxHeight ? (
                    <div className='genericPopupContentWrapper'>
                        <div className='genericPopup genericPopupContent'>{children}</div>
                    </div>
                ) : (
                    <div className='genericPopup genericPopupContent'>{children}</div>
                )}
                <div className={`genericPopup genericPopupActions ${buttonAlignment}`}>
                    {actionButtons?.map((b) => (
                        <Button
                            id={b.id}
                            key={b.id}
                            theme={b.theme}
                            disabled={b.disabled}
                            handleClick={b.handleClick}
                            hint={b.hint}
                            width={b.width}
                        >
                            {b.children}
                        </Button>
                    ))}
                </div>
            </Popup>
        </>
    );
};
